import { html, TemplateResult, PropertyValues, nothing } from 'lit';
import { property, customElement, state, query } from 'lit/decorators.js';
import { GzFormFieldWebComponent } from '../../gzForm/formField';
import { FormFieldInput } from '../../gzForm/formField/component/FormFieldInput';
import { ButtonColor, ButtonFill, ButtonSize, ButtonType, FontSize } from '../../gzIconButton/types';
import { GzInputWebComponent } from '../../gzInput/component/GzInputWebComponent';
import { styles } from '../view/styles';
import { FormEvent } from '../../gzForm/formField/types/form-event.enum';
import { debounce } from '../../../utils/event.utils';
import { isElementOverflowed } from '../../../utils/htmlElement.utils';
import { classMap } from 'lit/directives/class-map.js';
import { ButtonColors, ButtonTypes } from '../../gzButton/types';
import { GzDialogWebComponent } from '../../gzDialog';
import { GzButtonWebComponent } from '../../gzButton';
import { InputTypes } from '../../gzInput/types';
import { GzInputSearchWebComponent } from '../../gzInput';
import { ChipsGeneratorStrings } from '../types';
import { ifDefined } from 'lit/directives/if-defined.js';
import { translateFn } from '../../../utils/translator.helper';
import { FontSizes, Keys } from '../../../types/common/types';
import { FormValidator, ValidationResult } from '../../gzForm/formField/types/form-validator';

@customElement('gz-chips-generator-input')
export class GzChipsGeneratorInputWebComponent extends FormFieldInput<string[]> {
  /**
   * If set to true, the input will not be added automatically to the chip list on blur or enter key press.
   * Usefully when chip list is controlled from outside.
   * Works with themeInput = true.
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  manualAdd = false;

  /**
   * Form Validators for chips
   */
  @property({ type: Array, attribute: true, reflect: true })
  validators: FormValidator<string>[] = [];

  /**
   * Validation regex for chips
   */
  @property({ type: String, attribute: true, reflect: true })
  validationRegex = '';

  @property({ type: Boolean, attribute: true, reflect: true })
  hideCopyToClipboardButton = false;

  /**
   * Input label type
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  hasFloatingLabel = false;

  @property({ type: Array, attribute: true, reflect: true })
  chipList: string[] = [];

  /**
   * Chips list can have a counter
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  showMaxNrOfItems = false;

  /**
   * User can add the same chip multiple times
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  allowDuplicates = false;

  /**
   * Chips looks like an input. When themeInput is set to true, the search functionality,
   * clear list button and chips counter won't be available anymore.
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  themeInput = false;

  /**
   * Chips input validation
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  isInvalidChipForm;

  /**
   * maxItems shows how many chips an user can add to chipList (when showMaxNrOfItems = true)
   */
  @property({ type: Number, attribute: true, reflect: true })
  maxItems = 100;

  /**
   * The maximum number of characters that can be entered in the input/textarea field.
   */
  @property({ type: Number, attribute: true, reflect: true })
  inputMaxChars: number;

  /**
   * Whether the uniqueness validation considers both uppercase and lowercase characters as equal.
   * When true, all chips are displayed with lowercase characters (useful for email addresses).
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  isCaseInsensitive = false;

  /**
   * If inputLines is greater than 1, the input will be a textarea. In this case, set themeInput to FALSE.
   */
  @property({ type: Number, attribute: true, reflect: true })
  inputLines = 1;

  /**
   * If this attribute is set to true, then the last chip element from the list can't be removed
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  required = false;

  /**
   * If this attribute is set to true, then you can't add new elements to the list
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  disabled = false;

  /**
   * The strings used in this component in inputs, buttons, etc. They need to be translated using translateFn!
   * The function <code>getDefaultDataTableStrings()</code> can be used to get the default strings
   */
  @property({ type: Object, attribute: true, reflect: true })
  strings: ChipsGeneratorStrings = {};

  /**
   * This property is used to show the search input field. Its value is set based on the chipList length. If the chipList length is greater than 9, the search input field will be displayed.
   * @private
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  private hasSearchInput = false;

  @property({ type: Array, attribute: true, reflect: true })
  customKeyUps: Array<Keys> = [];

  @query('.chip-list')
  private chipInput: HTMLElement;

  @query('#search-input')
  private searchInput: GzInputSearchWebComponent;

  @state() private copyToClipboardChipListString: string;

  @query('#chips-generator-form-field')
  private chipsGeneratorFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;

  /**
   * disabled state for add/plus button
   */
  @state() private addBtnDisabled = true;

  @state() private currentChipList: string[] = [];

  @state() private filteredChipList: string[] = [];

  @state() private isEmptySearch = true;

  private itemsListTempCopy: string[] = [];

  private readonly SEPARATOR = ',';

  private readonly INPUTSEPARATOR = ';';

  private readonly INPUT_HEIGHT = 32;

  static get styles() {
    return styles;
  }

  public setFormValue(list: string[]): void {
    this.currentChipList = [...list];
    this.filterChipList();
  }

  public getFormValue(): string[] {
    return this.currentChipList;
  }

  firstUpdated(changedProperties: PropertyValues): void {
    super.firstUpdated(changedProperties);

    if (changedProperties.has('chipList')) {
      this.currentChipList = [...this.chipList];
    }

    this.filterChipList();
  }

  updated(changedProperties: PropertyValues): void {
    if (changedProperties.has('maxItems')) {
      this.sliceChipsListIfNeeded();
    } else if (changedProperties.has('currentChipList')) {
      this.filteredChipList = [...this.currentChipList];

      if (this.searchInput?.value) {
        this.onSearchInputChange();
      }
      this.updateCopyToClipboardString();
    } else if (changedProperties.has('filteredChipList')) {
      this.updateCopyToClipboardString();
    }
  }

  render(): TemplateResult {
    if (this.themeInput) {
      return html`<div class="d-flex input-wrapper">${this.renderChipsContainer()}</div>
        ${this.renderHintText()}`;
    }

    return html`
      <div class="d-flex input-wrapper">${this.renderTextInputFormField()} ${this.renderGenerateChipsButton()}</div>
      ${this.currentChipList?.length > 0 ? this.renderChipsContainer() : ''}
    `;
  }

  connectedCallback(): void {
    this.addEventListener(FormEvent.FormInvalid, this.markInvalid);
    this.addEventListener(FormEvent.FormValid, this.markValid);
    super.connectedCallback();
  }

  disconnectedCallback(): void {
    this.removeEventListener(FormEvent.FormInvalid, this.markInvalid);
    this.removeEventListener(FormEvent.FormValid, this.markValid);
    super.disconnectedCallback();
  }

  private clearTextArea(): void {
    this.chipsGeneratorFormField?.Input?.setFormValue('');
  }

  private renderInputField(): TemplateResult {
    if (this.inputLines > 1) {
      return html`
        <gz-textarea
          style="--max-height-textarea: ${this.INPUT_HEIGHT * this.inputLines}px;"
          id="gz-input-chips"
          gz-form-input
          label="${this.mustShowFloatingLabel() ? this.strings.label : nothing}"
          placeholder="${this.strings.placeholder}"
          @keyup="${this.onKeyUp}"
          @change="${this.handleInputChange}"
          @blur="${this.handleInputBlur}"
          .rows="${this.inputLines}"
          .maxChars="${ifDefined(this.inputMaxChars)}"
          ?disabled="${this.disabled}"
        ></gz-textarea>
        <gz-icon-button
          style="${!this.chipsGeneratorFormField?.value ? 'display: none' : 'display: block'}"
          id="deleteButton"
          class="clear-button"
          icon="ds-icon-clear"
          size="24"
          fontsize="20"
          buttontype="square"
          buttonfill="quiet"
          buttoncolor="secondary"
          @click="${this.clearTextArea}"
        >
        </gz-icon-button>
      `;
    }

    return html`<gz-input
      id="gz-input-chips"
      gz-form-input
      label="${this.mustShowFloatingLabel() ? this.strings.label : nothing}"
      type="text"
      ?disabled="${this.disabled}"
      canDelete="false"
      inputWidth="${this.themeInput ? '' : 'full'}"
      placeholder="${this.strings.placeholder}"
      .maxLength="${ifDefined(this.inputMaxChars)}"
      @keyup="${this.onKeyUp}"
      @change="${this.handleInputChange}"
      @blur="${this.handleInputBlur}"
      ?disabled="${this.disabled}"
    ></gz-input>`;
  }

  private mustShowFloatingLabel(): boolean {
    return this.hasFloatingLabel && !this.themeInput;
  }

  private renderTextInputFormField(): TemplateResult {
    return html`<gz-form-field
      id="chips-generator-form-field"
      part="chips-generator-form-field"
      label="${!this.hasFloatingLabel && !this.themeInput ? this.strings.label : nothing}"
      manualValidation
      ?disabled="${this.disabled}"
    >
      ${this.renderInputField()} ${!this.themeInput ? this.renderHintText() : nothing}${this.renderError()}
    </gz-form-field>`;
  }

  private renderGenerateChipsButton(): TemplateResult {
    return html`<gz-icon-button
      id="add-chips-button"
      icon="ds-icon-add"
      ?disabled="${this.addBtnDisabled || this.disabled}"
      fontSize="${FontSize.ICON_SIZE_24}"
      size="${ButtonSize.SIZE_32}"
      buttonFill="${ButtonFill.PRIMARY}"
      buttonType="${ButtonType.SQUARE}"
      buttonColor="${ButtonColor.WHITE}"
      @click="${this.handleGenerateChipsBtnClick}"
    ></gz-icon-button>`;
  }

  private renderError(): TemplateResult | undefined {
    if (this.themeInput) {
      return;
    }

    return html` <gz-input-error></gz-input-error>`;
  }

  private renderHintText(): TemplateResult | undefined {
    if (!this.strings.hintText) {
      return;
    }

    const classes = {
      'hint-text': true,
      'visible-hint': !this.isInvalidChipForm,
    };

    return html`<gz-text fontSize="${FontSizes.PARAGRAPH_12}" lineHeight="1" class="${classMap(classes)}"
      >${this.strings.hintText}</gz-text
    >`;
  }

  private renderChipsContainer(): TemplateResult | undefined {
    if ((!this.currentChipList || this.currentChipList.length === 0) && !this.themeInput) {
      return;
    }

    const containerClasses = {
      'd-flex': true,
      'align-items-end': !this.themeInput,
    };

    const footerClasses = {
      'd-flex': true,
      'justify-end': this.themeInput,
      'justify-space-between actions': !this.themeInput,
    };

    return html`
      <div class="${classMap(containerClasses)}">
        <div class="input-list-container">
          ${this.renderChipList()}
          ${this.themeInput
            ? html`<div class="${classMap(footerClasses)}">${this.renderChipsCounter()}</div>`
            : nothing}
        </div>
        ${!this.hideCopyToClipboardButton ? this.renderCopyToClipboardBtn() : nothing}
      </div>
      ${!this.themeInput
        ? html`<div class="${classMap(footerClasses)}" part="footer">
            ${this.renderDeleteChipsButton()} ${this.renderChipsCounter()}
          </div>`
        : nothing}
    `;
  }

  private renderDeleteChipsButton(): TemplateResult {
    return html`<gz-button
      type="${ButtonTypes.QUIET}"
      color="${ButtonColor.NEGATIVE}"
      class="mt-05 ${!this.strings.clearListButtonText ? 'visibility-hidden' : ''}"
      @click="${this.showClearListDialog}"
      ?disabled="${this.hasNoResults() || this.disabled}"
      >${this.strings.clearListButtonText}</gz-button
    >`;
  }

  private showClearListDialog(): void {
    const windowBodyHtml = this.getWindowBodyHtml();

    const windowFooterHtml = this.getWindowFooterButtonsHtml();

    const windowTitle = this.strings.deleteWindowTitle;

    const dialog = GzDialogWebComponent.computeCustomDialog(
      windowBodyHtml,
      windowFooterHtml,
      windowTitle ?? '',
      null,
      async () => {
        await this.clearListAndCloseDialog(dialog);
      },
      null,
      false
    );
  }

  private async clearListAndCloseDialog(dialog: GzDialogWebComponent): Promise<void> {
    try {
      this.setConfirmButtonState(dialog, true, true);
      this.clearList();
      dialog.close();
    } catch (error) {
      console.error('Error clearing list and closing dialog', error);
    } finally {
      this.setConfirmButtonState(dialog, false, false);
    }
  }

  private setConfirmButtonState(dialog: GzDialogWebComponent, showSpinner: boolean, disabled: boolean): void {
    const confirmBtn = dialog.shadowRoot?.querySelector<GzButtonWebComponent>('#confirm');
    if (confirmBtn) {
      confirmBtn.showSpinner = showSpinner;
      confirmBtn.disabled = disabled;
    }
  }

  private getWindowBodyHtml(): string {
    return `<p>${this.strings.deleteWindowContentText}</p>`;
  }

  private getWindowFooterButtonsHtml(): string {
    return `
      <gz-button id='confirm' type='${ButtonTypes.OUTLINE}' color='${ButtonColors.NEGATIVE}'>
        ${this.strings.clearListActionBtnString}
      </gz-button>
      <gz-button id='cancel' type='${ButtonTypes.OUTLINE}' color='${ButtonColors.SECONDARY}'>
         ${this.strings.cancelClearListBtnString}
      </gz-button`;
  }

  private clearList(): void {
    if (this.searchInput?.value) {
      this.currentChipList = this.currentChipList.filter(chip => !this.filteredChipList.includes(chip));
      this.filteredChipList = [...this.currentChipList];
      this.searchInput.setFormValue('');
    } else {
      this.currentChipList = [];
    }

    this.emitChangeEvent();
  }

  private renderChipList(): TemplateResult {
    const list = !this.themeInput && !this.isEmptySearch ? this.filteredChipList : this.currentChipList;
    const chipListHtml = list?.map((chipText, chipIndex) => this.renderChipItem(chipText, chipIndex));

    return html`
      <div class="chip-list d-flex" part="chip-list">
        ${this.renderSearchBar()} ${this.renderNoSearchResultText()}
        ${this.themeInput ? chipListHtml : html`<div class="scrollable-container">${chipListHtml}</div>`}
        ${this.themeInput ? this.renderTextInputFormField() : nothing}
      </div>
    `;
  }

  private renderNoSearchResultText(): TemplateResult {
    if (this.themeInput || !this.hasNoResults()) {
      return html``;
    }

    return html`<gz-text fontsize="${FontSizes.PARAGRAPH_12}" class="no-results-string"
      >${this.strings.noSearchResultsString}</gz-text
    >`;
  }

  private hasNoResults(): boolean {
    return !this.isEmptySearch && this.filteredChipList.length === 0;
  }

  private renderSearchBar(): TemplateResult {
    if (this.themeInput || !this.hasSearchInput) {
      return html``;
    }

    return html`<gz-input-search
      id="search-input"
      class="search-input"
      part="search-input"
      type="${InputTypes.TEXT}"
      ?canDelete="false"
      ?disabled="${this.disabled}"
      inputWidth="auto"
      placeholder="${this.strings.searchPlaceholder}"
      @change="${this.onSearchInputChange}"
    ></gz-input>`;
  }

  private onSearchInputChange(): void {
    const filterValue = this.searchInput?.value.toLowerCase();
    this.isEmptySearch = !filterValue;
    this.filteredChipList = this.currentChipList.filter(chipText => chipText.toLowerCase().includes(filterValue));
  }

  private renderChipsCounter(): TemplateResult | undefined {
    if (!this.showMaxNrOfItems && this.themeInput) {
      return;
    }

    return html`<span class="fs-12 counter">${this.renderCounterText()}</span>`;
  }

  private renderCounterText(): TemplateResult {
    const list = this.searchInput?.value ? this.filteredChipList : this.currentChipList;
    const baseCount = `${list?.length ?? 0}`;
    const shouldShowMaxItems = this.themeInput || this.showMaxNrOfItems || this.searchInput?.value;
    const maxItems = !this.searchInput?.value ? this.maxItems : this.currentChipList.length;
    const countString = shouldShowMaxItems ? `${baseCount}/${maxItems}` : baseCount;
    return html`${countString}`;
  }

  private renderCopyToClipboardBtn(): TemplateResult {
    return html`<gz-copy-to-clipboard
      .tooltipText="${this.disabled ? '' : translateFn('Copy to clipboard')}"
      confirmationMessage="${translateFn('Copied')}"
      ?disabled="${this.hasNoResults() || this.disabled}"
      ><span class="hidden">${this.copyToClipboardChipListString}</span></gz-copy-to-clipboard
    >`;
  }

  private renderChipItem(chipText: string, chipIndex: number): TemplateResult {
    return html` <gz-chip
      text=${chipText}
      boldSubtext=${!this.themeInput && this.searchInput ? this.searchInput?.value : ''}
      notSelectable
      ?disabled=${this.disabled}
      .removable=${!this.required || this.currentChipList.length > 1}
      @removeChip="${() => this.handleRemoveItem(chipIndex)}"
    ></gz-chip>`;
  }

  private handleRemoveItem(chipIndex: number): void {
    if (this.required && this.currentChipList.length <= 1) {
      return;
    }

    let chipIndexToRemove = chipIndex;
    // a search was made, so we need to remove the item from the filtered list
    if (!this.themeInput && !this.isEmptySearch) {
      const newFilteredItems = this.filteredChipList?.slice();
      // remove the item from the filtered list
      const removedItem = newFilteredItems?.splice(chipIndexToRemove, 1)[0];
      this.filteredChipList = newFilteredItems;

      // find the corresponding item from the currentChipList
      chipIndexToRemove = this.currentChipList.findIndex(
        (chip, index) => chip === removedItem && !this.filteredChipList.includes(chip, index)
      );
    }

    const newItems = this.currentChipList?.slice();
    const itemsToRemove = newItems?.splice(chipIndexToRemove, 1);
    this.currentChipList = newItems;

    this.emitChangeEvent({ removedItem: itemsToRemove[0] });
  }

  private onKeyUp(event: KeyboardEvent): void {
    if (this.customKeyUps.length) {
      if (this.customKeyUps.includes(event.key as Keys)) {
        this.handleGenerateChipsBtnClick();
        return;
      }
    }
    if (event.key === Keys.Enter) {
      this.handleGenerateChipsBtnClick();
    }
  }

  private handleInputBlur(): void {
    if (!this.themeInput) {
      return;
    }

    this.handleGenerateChipsBtnClick();
  }

  public handleScrollToInput(): void {
    const scrollHeight = this.chipInput.scrollHeight;

    const debounceType = debounce(() => {
      if (isElementOverflowed(this.chipInput)) {
        this.chipInput.scrollTo(0, scrollHeight);
      }
    }, 300);
    debounceType();
  }

  private handleInputChange(): void {
    this.addBtnDisabled = !this.chipsGeneratorFormField?.Input.value;

    this.dispatchEvent(
      new CustomEvent('inputChange', {
        detail: {
          value: this.chipsGeneratorFormField?.Input.value,
        },
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }

  private async handleGenerateChipsBtnClick(): Promise<void> {
    if (!this.chipsGeneratorFormField?.value) return;

    // if manualAdd is true, we don't want to generate chips from the input value
    // we want to keep the input value as it is
    if (this.themeInput && this.manualAdd) {
      return;
    }

    const itemsList = this.getSplitedItemsFromInputValue();

    this.itemsListTempCopy = [...itemsList];

    await this.generateChipsFromList(itemsList);

    if (this.themeInput) {
      this.handleScrollToInput();
    }
  }

  private async generateChipsFromList(itemsList: string[]): Promise<void> {
    const currentChipListCopy = [...this.currentChipList];
    let hasChanged;
    let isError;
    let errorToDisplay;

    [isError, hasChanged, errorToDisplay] = await this.traverseItemsList(itemsList, currentChipListCopy);

    if (hasChanged) {
      this.currentChipList = [...currentChipListCopy];
      this.setTextInputValue(this.joinItems(this.itemsListTempCopy));
      this.emitChangeEvent();
    }

    if (!isError) {
      this.setTextInputValue('');
    } else {
      this.chipsGeneratorFormField.setError(errorToDisplay);
    }
  }

  private async traverseItemsList(itemsList: string[], currentChipListCopy: string[]): Promise<any[]> {
    let hasChanged = false;
    let isError = false;
    let errorToDisplay = this.strings.invalidItemErrorMessage;

    for (let item of itemsList) {
      let canAddItem = false;
      const originalItem = item;
      item = this.isCaseInsensitive ? item.toLowerCase() : item;

      if (this.showMaxNrOfItems && currentChipListCopy?.length >= this.maxItems) {
        isError = true;
        errorToDisplay = this.strings.limitReachedErrorMessage;
        break;
      } else if (
        (!this.allowDuplicates && this.existChipsInList(currentChipListCopy, item)) ||
        !this.isMatchingRegex(item)
      ) {
        isError = true;
        if (
          !this.allowDuplicates &&
          this.existChipsInList(currentChipListCopy, item) &&
          this.strings.notUniqueErrorMessage
        ) {
          errorToDisplay = this.strings.notUniqueErrorMessage;
        }
      } else {
        const result = await this.validateItem(item);
        if (!result.valid) {
          isError = true;
          errorToDisplay = result.error;
        } else {
          canAddItem = true;
        }
      }

      if (canAddItem) {
        currentChipListCopy.push(item);
        hasChanged = true;

        // the item is added, so we can remove the item from the input value
        this.removeItemFromInputValue(originalItem);
      }
    }

    return [isError, hasChanged, errorToDisplay];
  }

  private async validateItem(item: string): Promise<ValidationResult> {
    for (const validator of this.validators) {
      const result = await validator(item);
      if (!result.valid) {
        return result;
      }
    }
    return { valid: true };
  }

  private removeItemFromInputValue(trimedText: string): void {
    this.itemsListTempCopy?.splice(this.itemsListTempCopy.indexOf(trimedText), 1);
  }

  private setTextInputValue(value: string): void {
    this.chipsGeneratorFormField?.Input?.setFormValue(value);
  }

  private getSplitedItemsFromInputValue(): string[] {
    return this.chipsGeneratorFormField?.value
      .split(!this.themeInput ? this.SEPARATOR : this.INPUTSEPARATOR)
      .map(item => item.trim())
      .filter(item => item !== '');
  }

  private existChipsInList(currentChipList: string[], chipText: string): boolean {
    return Boolean(currentChipList?.find(item => item === chipText));
  }

  private isMatchingRegex(text: string): boolean {
    return !this.validationRegex || Boolean(text.match(this.validationRegex));
  }

  private updateCopyToClipboardString(): void {
    this.copyToClipboardChipListString = this.joinItems(this.themeInput ? this.currentChipList : this.filteredChipList);
  }

  private joinItems(list: string[]): string {
    return list?.map(chip => chip).join(!this.themeInput ? this.SEPARATOR + ' ' : this.INPUTSEPARATOR + ' ');
  }

  private emitChangeEvent(extraDetails?: object): void {
    const event = new CustomEvent('change', {
      detail: {
        value: this.currentChipList,
        ...extraDetails,
      },
      bubbles: true,
      cancelable: true,
      composed: true,
    });
    this.hasSearchInput = this.currentChipList.length >= 9;
    this.dispatchEvent(event);
  }

  private filterChipList(): void {
    if (this.currentChipList?.length > 0) {
      this.handleChipListForCaseSensitivity();

      // remove duplicates
      if (!this.allowDuplicates) {
        this.currentChipList = [...new Set(this.currentChipList)];
      }

      // validate items
      this.currentChipList = this.currentChipList.filter(item => this.isMatchingRegex(item));

      this.sliceChipsListIfNeeded();

      this.emitChangeEvent();
    }
  }

  private handleChipListForCaseSensitivity(): void {
    if (this.isCaseInsensitive) {
      this.currentChipList = this.currentChipList.map(chip => chip.toLowerCase());
    }
  }

  private sliceChipsListIfNeeded(): void {
    if (this.showMaxNrOfItems && this.currentChipList?.length >= this.maxItems) {
      this.currentChipList = this.currentChipList.slice(0, this.maxItems);
    }
  }

  private markInvalid(): void {
    this.isInvalidChipForm = true;
  }

  private markValid(): void {
    this.isInvalidChipForm = false;
  }

  public setInputError(errorMessage): void {
    this.isInvalidChipForm = true;
    this.chipsGeneratorFormField.setError(errorMessage);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-chips-generator-input': GzChipsGeneratorInputWebComponent;
  }
}
